/* eslint-disable @nx/enforce-module-boundaries */
/* eslint-disable no-restricted-imports */
import { memo, Suspense, useEffect, useMemo, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { type NextPage } from 'next'
import dynamic from 'next/dynamic'
import { Spinner } from '@stone-payments/jade'
import curry from 'lodash.curry'
import { isomorphicCookies } from 'shared/storage'

import { authenticator } from '~/domains/platform/core/authenticator/authenticator'
import { type OptIns, type WithDeusExMachinaProps } from '~/domains/platform/infra/deus-ex-machina/ports'
import { type WithPageConfigProps } from '~/domains/platform/infra/page-enhancers/with-page-config'
import { Bucket } from '~/domains/platform/lib/bucket'

import '../utils/ton-tokens-side-effects'

import { PreProcessedFontStyles } from '../styles'
import { WebviewNativeStyles } from '../styles/webview-native-styles'
import { durationInMs } from '../utils/duration-in-ms'
import { useWebviewNativeEnhancements } from '../utils/use-webview-native-enhancements'
import {
  cancelTokenRefresh,
  isPlaygroundMode,
  offTokenExpired,
  onTokenExpired,
  redirectToLoadUser,
  scheduleTokenRefresh,
  webviewRuntime
} from '../utils/webview-runtime'

export const JadeProvider = dynamic(() => import('@stone-payments/jade').then(mod => mod.JadeProvider), { ssr: false })

const loadingContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  gap: 16
}

const loadingTextStyle: React.CSSProperties = {
  fontSize: 14,
  color: '#999',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
}

function WebviewLoadingFallback() {
  const [showText, setShowText] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setShowText(true), 600)
    return () => clearTimeout(timer)
  }, [])

  return (
    <div style={loadingContainerStyle}>
      <Spinner size="medium" color="brand" />
      {showText && <span style={loadingTextStyle}>Carregando...</span>}
    </div>
  )
}

type WithWebViewProp = {
  isInsideWebView?: boolean
  webviewRuntime?: typeof webviewRuntime
  appTheme?: 'stone-rebrand' | 'stone' | 'ton'
}
export type WithWebviewArgs = {
  timeoutMs?: number
  withAuth?: boolean
}
export type WithWebviewPageConfigProps<T extends OptIns = never> = WithPageConfigProps<T> & WithWebViewProp

const WebviewErrorBoundary = memo(function WebviewErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary
      fallbackRender={() => null}
      onError={error => {
        console.error('[WebviewErrorBoundary] WebView render error:', error)
        if (typeof window !== 'undefined' && webviewRuntime.webviewErrorHandler) {
          webviewRuntime.webviewErrorHandler(error)
        }
      }}
    >
      {children}
    </ErrorBoundary>
  )
})

type WithWebviewPageConfigType = {
  Page: NextPage<any>
  webview: WithWebviewArgs
}

const WithWebviewPageConfigInner = memo(function WithWebviewPageConfig({ Page, ...props }: WithWebviewPageConfigType) {
  const timeoutMs = props?.webview?.timeoutMs
  const withAuth = props?.webview?.withAuth
  const { isInsideWebView, theme, isLoading } = useWebviewSessionManager({ timeoutMs, withAuth })

  useWebviewNativeEnhancements(isInsideWebView)

  const pageProps = useMemo(
    () => ({ ...props, isInsideWebView, webviewRuntime, appTheme: theme }),
    [props, isInsideWebView, theme]
  )

  const pageElement = useMemo(
    () => (
      <WebviewErrorBoundary>
        <Suspense fallback={<WebviewLoadingFallback />}>
          <Page {...pageProps} />
        </Suspense>
      </WebviewErrorBoundary>
    ),
    [Page, pageProps]
  )

  if (isLoading) {
    return <WebviewLoadingFallback />
  }

  return (
    <Suspense fallback={<WebviewLoadingFallback />}>
      <PreProcessedFontStyles />
      <WebviewNativeStyles isInsideWebView={isInsideWebView} />
      <JadeProvider theme={theme as any}>{pageElement}</JadeProvider>
    </Suspense>
  )
})

type Props = {
  timeoutMs?: number
  withAuth?: boolean
}

function useWebviewSessionManager(props?: Props) {
  const timeoutMs = props?.timeoutMs ?? durationInMs(10)
  const shouldAuthenticateUser = props?.withAuth ?? true

  const [isWebviewValid, setIsWebviewValid] = useState(true)
  const [isLoading, setIsLoading] = useState(true)

  const cookies = typeof window !== 'undefined' ? isomorphicCookies.getAll() : null
  const theme = cookies?.['appPlatform'] === 'ton' ? 'ton' : 'stone-rebrand'
  const { isWebView } = cookies ?? { isWebView: false }

  useEffect(() => {
    const rafId = requestAnimationFrame(() => setIsLoading(false))
    return () => cancelAnimationFrame(rafId)
  }, [])

  useEffect(() => {
    if (!shouldAuthenticateUser || !isWebView) return
    redirectToLoadUser()
  }, [isWebView, shouldAuthenticateUser])

  useEffect(() => {
    if (!isWebView) return

    const startTime = Date.now()

    const timeoutId = setTimeout(() => {
      console.info(`[WebviewSessionManager] WebView session timeout reached (${Math.round(timeoutMs / 1000)}s)`)
      setIsWebviewValid(false)
      webviewRuntime.webviewFinishMethod()
    }, timeoutMs)

    const logIntervalId = setInterval(() => {
      const elapsedMs = Date.now() - startTime
      const remainingMs = timeoutMs - elapsedMs
      console.info(
        `[WebviewSessionManager] Elapsed: ${Math.round(elapsedMs / 1000)}s / ${Math.round(timeoutMs / 1000)}s` +
          ` (${Math.round(remainingMs / 1000)}s remaining)`
      )
    }, 30_000)

    return () => {
      clearTimeout(timeoutId)
      clearInterval(logIntervalId)
    }
  }, [isWebView, timeoutMs])

  useEffect(() => {
    if (!shouldAuthenticateUser || !isWebView) return

    onTokenExpired(() => {
      console.error('[WebviewSessionManager] Token expired — blocking UI')
      setIsWebviewValid(false)
    })

    scheduleTokenRefresh()

    return () => {
      cancelTokenRefresh()
      offTokenExpired()
    }
  }, [isWebView, shouldAuthenticateUser])

  useEffect(() => {
    if (!isWebView || !isPlaygroundMode()) return

    async function refreshTokensViaSDK() {
      try {
        if (authenticator.isSessionValid()) return

        console.info('[WebviewSessionManager] Playground: refreshing expired token via SDK')
        const deviceGeneratedId = Bucket.local.get('device::generated::id')
        const platformId = Bucket.local.get('platform::id')
        await authenticator.revalidateSession(undefined, {
          header: { deviceGeneratedId, platformId }
        })
        console.info('[WebviewSessionManager] Playground: token refreshed successfully via SDK')
        scheduleTokenRefresh()
      } catch (err) {
        console.error('[WebviewSessionManager] Playground: SDK token refresh failed:', err)
      }
    }

    refreshTokensViaSDK()
  }, [isWebView])

  return { isInsideWebView: isWebviewValid, theme, isLoading }
}

export const withWebviewPageConfig = curry(function (args: WithWebviewArgs, Page: NextPage<WithDeusExMachinaProps>) {
  const WrappedComponent = (props: any) => <WithWebviewPageConfigInner {...props} Page={Page} webview={args} />

  const ClientSidePage = dynamic(() => Promise.resolve(WrappedComponent), {
    ssr: false,
    loading: () => <WebviewLoadingFallback />
  })

  return ClientSidePage
})
